import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'rizzui';
import { toast } from 'sonner';
import Swal from 'sweetalert2';
import ModalForm from '../../../components/forms/ModalForm';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { PiPhoneCallFill } from "react-icons/pi";
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { CallsService } from '../../../services/calls/callsService';

interface CallModalFormProps {
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
    contractId?: string;
    currentClientId?: string;
    phone: string;
    phoneSecondary: string;
    mobile: string;
}

interface DuplicateClientOption {
    id: string;
    companyName: string;
    cifNif: string;
    telephone: string | null;
    telephoneSecondary: string | null;
    mobile: string | null;
    active: boolean | null;
    companyNameOwner: string | null;
}

interface DuplicateUserOption {
    id: string;
    fullName: string;
    email: string;
    companyName: string | null;
}

const CallModalForm: React.FC<CallModalFormProps> = ({
    isOpen,
    onClose,
    refetch,
    contractId,
    currentClientId,
    phone,
    phoneSecondary,
    mobile,
}) => {

    const { handleErrors } = useHandleErrors();
    const [isLoading, setIsLoading] = useState<string | null>(null);
    const [duplicateClients, setDuplicateClients] = useState<DuplicateClientOption[]>([]);
    const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
    const [pendingPhone, setPendingPhone] = useState<string | null>(null);
    const user = useSelector((state: RootState) => state.auth.user);
    const ocmUserLogin = user?.ocmUserLogin;

    const resetDuplicateSelection = () => {
        setDuplicateClients([]);
        setSelectedClientId(null);
        setPendingPhone(null);
    };

    useEffect(() => {
        if (!isOpen) {
            resetDuplicateSelection();
            setIsLoading(null);
        }
    }, [isOpen]);

    const selectedDuplicateClient = useMemo(
        () => duplicateClients.find((client) => client.id === selectedClientId) ?? null,
        [duplicateClients, selectedClientId]
    );

    const showDuplicateUserModal = async (users: DuplicateUserOption[]) => {
        const items = users
            .map((userItem) => {
                const details = [userItem.fullName, userItem.email, userItem.companyName].filter(Boolean).join(' · ');
                return `<li>${details}</li>`;
            })
            .join('');

        await Swal.fire({
            title: 'Login de usuario duplicado',
            html: `
                <p style="margin-bottom: 12px;">Hay varios usuarios del sistema con el mismo login de usuario. Debe corregirse antes de realizar la llamada.</p>
                <ul style="text-align: left; padding-left: 20px;">${items}</ul>
            `,
            icon: 'error',
            confirmButtonColor: '#009737',
            confirmButtonText: 'Cerrar',
        });
    };

    const realizeCall = async (number: string, clientId?: string | null) => {
        setIsLoading(clientId ? `selected-${clientId}` : number);

        try {
            if (!ocmUserLogin) {
                toast.error('Tu usuario no tiene asignado ningún usuario de OCM');
                return;
            }

            const response = await (await new CallsService().realizeCall(number, ocmUserLogin, clientId, contractId ?? null)).getResponseData();

            if (response.success) {
                toast.success(`Llamando al ${number}. Accede a la aplicación de OCM para continuar la llamada.`);
                resetDuplicateSelection();
                refetch();
                onClose();
                return;
            }

            if (response.data?.status === 'duplicate_ocm_user_login') {
                await showDuplicateUserModal(response.data?.users ?? []);
                return;
            }

            if (response.data?.status === 'select_client') {
                const clients = (response.data?.clients ?? []) as DuplicateClientOption[];
                const defaultClient =
                    clients.find((client) => client.id === currentClientId && client.active) ??
                    clients.find((client) => client.active) ??
                    clients[0] ??
                    null;

                setPendingPhone(response.data?.phone ?? number);
                setDuplicateClients(clients);
                setSelectedClientId(defaultClient?.id ?? null);
                return;
            }

            handleErrors(response);
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsLoading(null);
        }
    };

    const phones = [
        { label: "Teléfono principal", number: phone },
        { label: "Teléfono secundario", number: phoneSecondary },
        { label: "Móvil", number: mobile }
    ].filter(p => p.number && p.number.trim() !== "");

    return (
        <ModalForm isOpen={isOpen} onClose={onClose} title="Realizar llamada a cliente">
            {duplicateClients.length > 0 && pendingPhone ? (
                <div className="space-y-4 pb-4">
                    <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900">
                        Hay varios clientes con el número <span className="font-semibold">{pendingPhone}</span>. Selecciona con cuál quieres continuar la llamada.
                    </div>

                    <div className="space-y-2">
                        {duplicateClients.map((client) => {
                            const isSelected = client.id === selectedClientId;

                            return (
                                <button
                                    key={client.id}
                                    type="button"
                                    disabled={!client.active}
                                    onClick={() => setSelectedClientId(client.id)}
                                    className={`w-full rounded-lg border p-4 text-left transition-colors ${
                                        client.active
                                            ? isSelected
                                                ? 'border-primary bg-primary/5'
                                                : 'border-gray-200 bg-white hover:border-gray-300'
                                            : 'cursor-not-allowed border-gray-200 bg-gray-100 opacity-60'
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="space-y-1">
                                            <p className="text-base font-semibold text-gray-900">{client.companyName}</p>
                                            <p className="text-sm text-gray-600">{client.cifNif}</p>
                                            <p className="text-sm text-gray-600">
                                                Empresa: {client.companyNameOwner || 'Sin empresa'}
                                            </p>
                                            <p className="text-sm text-gray-600">
                                                {client.telephone || '-'} · {client.telephoneSecondary || '-'} · {client.mobile || '-'}
                                            </p>
                                        </div>
                                        <span className={`text-xs font-semibold ${client.active ? 'text-green-700' : 'text-red-700'}`}>
                                            {client.active ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex justify-end gap-3">
                        <Button variant="outline" onClick={resetDuplicateSelection} disabled={isLoading !== null}>
                            Volver
                        </Button>
                        <Button
                            color="primary"
                            isLoading={isLoading === `selected-${selectedClientId}`}
                            disabled={!selectedDuplicateClient?.active || isLoading !== null}
                            onClick={() => realizeCall(pendingPhone, selectedClientId)}
                        >
                            Continuar llamada
                        </Button>
                    </div>
                </div>
            ) : (
                <>
                    {phones.length === 0 && (
                        <p className="text-center py-4 text-gray-600">
                            No hay teléfonos disponibles para este cliente.
                        </p>
                    )}

                    <div className="space-y-2 pb-4">
                        {phones.map((p, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between border rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                            >
                                <div>
                                    <p className="text-sm text-gray-600">{p.label}</p>
                                    <p className="text-lg font-semibold text-gray-900">{p.number}</p>
                                </div>

                                <Button
                                    size="sm"
                                    color="primary"
                                    isLoading={isLoading === p.number}
                                    disabled={isLoading !== null}
                                    onClick={() => realizeCall(p.number)}
                                    className="!w-10 !h-10 flex items-center justify-center"
                                >
                                    <PiPhoneCallFill className="w-5 h-5" />
                                </Button>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </ModalForm>
    );
};

export default CallModalForm;
